import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import FiscalYearVariantForm from '../components/FiscalYearVariantForm';
import ChartOfAccountsForm from '../components/ChartOfAccountsForm';
import { Plus } from 'lucide-react';
import { financeAPI, masterDataAPI } from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Card } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { useSearchParams } from 'react-router-dom';
import { RecordActions } from '../components/common/RecordActions';
import { RecordViewDialog } from '../components/common/RecordViewDialog';
import { useAuth } from '../context/AuthContext';

const Finance = () => {
  const { canWrite } = useAuth();
  const [searchParams] = useSearchParams();
  const tabFromUrl = searchParams.get('tab') || 'credit-control-areas';
  const [activeTab, setActiveTab] = useState(tabFromUrl);
  const [data, setData] = useState({
    creditControlAreas: [],
    businessAreas: [],
    consolidatedBusinessAreas: [],
    functionalAreas: [],
    segments: [],
    chartOfAccounts: [],
    fiscalYearVariants: [],
  });
  const [currencies, setCurrencies] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [viewItem, setViewItem] = useState(null);
  
  // Form-specific state (moved from getFormFields)
  const [selectedCurrency, setSelectedCurrency] = useState('');
  const [selectedYearType, setSelectedYearType] = useState('');

  // Update form state when editingItem changes
  useEffect(() => {
    if (editingItem) {
      setSelectedCurrency(editingItem?.currency || editingItem?.area_currency || '');
      setSelectedYearType(editingItem?.year_type || '');
    } else {
      setSelectedCurrency('');
      setSelectedYearType('');
    }
  }, [editingItem]);

  useEffect(() => {
    setActiveTab(tabFromUrl);
  }, [tabFromUrl]);

  useEffect(() => {
    loadData();
    loadCurrencies();
  }, []);

  const loadCurrencies = async () => {
    try {
      const response = await masterDataAPI.getCurrencies();
      setCurrencies(response.data);
    } catch (error) {
      console.error('Failed to load currencies:', error);
    }
  };

  const loadData = async () => {
    try {
      const [cca, ba, cba, fa, seg, coa, fyv] = await Promise.all([
        financeAPI.getCreditControlAreas(),
        financeAPI.getBusinessAreas(),
        financeAPI.getConsolidatedBusinessAreas(),
        financeAPI.getFunctionalAreas(),
        financeAPI.getSegments(),
        financeAPI.getChartOfAccounts(),
        financeAPI.getFiscalYearVariants(),
      ]);
      setData({
        creditControlAreas: cca.data,
        businessAreas: ba.data,
        consolidatedBusinessAreas: cba.data,
        functionalAreas: fa.data,
        segments: seg.data,
        chartOfAccounts: coa.data,
        fiscalYearVariants: fyv.data,
      });
    } catch (error) {
      toast.error('Failed to load data');
    }
  };

  const handleCOASubmit = async (coaData) => {
    try {
      const id = editingItem?.coa_id;
      if (editingItem) {
        await financeAPI.updateChartOfAccounts(id, coaData);
      } else {
        await financeAPI.createChartOfAccounts(coaData);
      }
      setIsDialogOpen(false);
      setEditingItem(null);
      loadData();
      toast.success(editingItem ? 'Updated successfully' : 'Created successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    }
  };

  const handleFYVSubmit = async (fyvData) => {
    try {
      const id = editingItem?.fiscal_year_variant_id;
      if (editingItem) {
        await financeAPI.updateFiscalYearVariant(id, fyvData);
      } else {
        await financeAPI.createFiscalYearVariant(fyvData);
      }
      setIsDialogOpen(false);
      setEditingItem(null);
      loadData();
      toast.success(editingItem ? 'Updated successfully' : 'Created successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const formDataObj = {};
    formData.forEach((value, key) => {
      // Convert numeric fields
      if (['credit_limit', 'number_of_postings', 'number_of_special_postings'].includes(key)) {
        formDataObj[key] = value ? parseFloat(value) : null;
      } else {
        formDataObj[key] = value;
      }
    });

    try {
      const id = editingItem?.[getIdField()];
      switch (activeTab) {
        case 'credit-control-areas':
          editingItem 
            ? await financeAPI.updateCreditControlArea(id, formDataObj)
            : await financeAPI.createCreditControlArea(formDataObj);
          break;
        case 'business-areas':
          editingItem
            ? await financeAPI.updateBusinessArea(id, formDataObj)
            : await financeAPI.createBusinessArea(formDataObj);
          break;
        case 'consolidated-business-areas':
          editingItem
            ? await financeAPI.updateConsolidatedBusinessArea(id, formDataObj)
            : await financeAPI.createConsolidatedBusinessArea(formDataObj);
          break;
        case 'functional-areas':
          editingItem
            ? await financeAPI.updateFunctionalArea(id, formDataObj)
            : await financeAPI.createFunctionalArea(formDataObj);
          break;
        case 'segments':
          editingItem
            ? await financeAPI.updateSegment(id, formDataObj)
            : await financeAPI.createSegment(formDataObj);
          break;
        case 'chart-of-accounts':
          editingItem
            ? await financeAPI.updateChartOfAccounts(id, formDataObj)
            : await financeAPI.createChartOfAccounts(formDataObj);
          break;
        case 'fiscal-year-variants':
          editingItem
            ? await financeAPI.updateFiscalYearVariant(id, formDataObj)
            : await financeAPI.createFiscalYearVariant(formDataObj);
          break;
      }
      setIsDialogOpen(false);
      setEditingItem(null);
      loadData();
      toast.success(editingItem ? 'Updated successfully' : 'Created successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    }
  };

  const handleDelete = async (id) => {    if (!window.confirm('Are you sure?')) return;

    try {
      switch (activeTab) {
        case 'credit-control-areas':
          await financeAPI.deleteCreditControlArea(id);
          break;
        case 'business-areas':
          await financeAPI.deleteBusinessArea(id);
          break;
        case 'consolidated-business-areas':
          await financeAPI.deleteConsolidatedBusinessArea(id);
          break;
        case 'functional-areas':
          await financeAPI.deleteFunctionalArea(id);
          break;
        case 'segments':
          await financeAPI.deleteSegment(id);
          break;
        case 'chart-of-accounts':
          await financeAPI.deleteChartOfAccounts(id);
          break;
        case 'fiscal-year-variants':
          await financeAPI.deleteFiscalYearVariant(id);
          break;
      }
      loadData();
      toast.success('Deleted successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Delete failed');
    }
  };

  const getIdField = () => {
    const idMap = {
      'credit-control-areas': 'credit_control_area',
      'business-areas': 'business_area',
      'consolidated-business-areas': 'consolidated_business_area',
      'functional-areas': 'functional_area',
      'segments': 'segment_id',
      'chart-of-accounts': 'coa_id',
      'fiscal-year-variants': 'fiscal_year_variant_id',
    };
    return idMap[activeTab];
  };

  const getFormFields = () => {
    switch (activeTab) {
      case 'credit-control-areas':
        return (
          <React.Fragment>
            <div>
              <Label>Credit Control Area ID</Label>
              <Input 
                name="credit_control_area" 
                defaultValue={editingItem?.credit_control_area} 
                required 
                disabled={!!editingItem}
                placeholder="e.g., CCA001"
                onChange={(e) => e.target.value = e.target.value.toUpperCase()}
                style={{ textTransform: 'uppercase' }}
              />
            </div>
            <div>
              <Label>Currency</Label>
              <Select value={selectedCurrency} onValueChange={setSelectedCurrency} required>
                <SelectTrigger>
                  <SelectValue placeholder="Select currency" />
                </SelectTrigger>
                <SelectContent>
                  {currencies.map(curr => (
                    <SelectItem key={curr.currency_code} value={curr.currency_code}>
                      {curr.currency_code} - {curr.currency_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <input type="hidden" name="currency" value={selectedCurrency} />
            </div>
            <div>
              <Label>Credit Limit</Label>
              <Input 
                name="credit_limit" 
                type="number" 
                step="0.01"
                min="0"
                defaultValue={editingItem?.credit_limit} 
                required 
                placeholder="e.g., 100000.00"
              />
            </div>
          </React.Fragment>
        );

      case 'business-areas':
        return (
          <React.Fragment>
            <div>
              <Label>Business Area ID</Label>
              <Input 
                name="business_area" 
                defaultValue={editingItem?.business_area} 
                required 
                disabled={!!editingItem}
                placeholder="e.g., BA001"
                onChange={(e) => e.target.value = e.target.value.toUpperCase()}
                style={{ textTransform: 'uppercase' }}
              />
            </div>
            <div>
              <Label>Name</Label>
              <Input name="name" defaultValue={editingItem?.name} required placeholder="e.g., Operations" />
            </div>
            <div>
              <Label>Description</Label>
              <Input name="description" defaultValue={editingItem?.description} placeholder="Optional description" />
            </div>
          </React.Fragment>
        );

      case 'consolidated-business-areas':
        return (
          <React.Fragment>
            <div>
              <Label>Consolidated Business Area ID</Label>
              <Input 
                name="consolidated_business_area" 
                defaultValue={editingItem?.consolidated_business_area} 
                required 
                disabled={!!editingItem}
                placeholder="e.g., CBA001"
                onChange={(e) => e.target.value = e.target.value.toUpperCase()}
                style={{ textTransform: 'uppercase' }}
              />
            </div>
            <div>
              <Label>Name</Label>
              <Input name="name" defaultValue={editingItem?.name} required placeholder="e.g., Group Operations" />
            </div>
            <div>
              <Label>Description</Label>
              <Input name="description" defaultValue={editingItem?.description} placeholder="Optional description" />
            </div>
          </React.Fragment>
        );

      case 'functional-areas':
        return (
          <React.Fragment>
            <div>
              <Label>Functional Area ID</Label>
              <Input 
                name="functional_area" 
                defaultValue={editingItem?.functional_area} 
                required 
                disabled={!!editingItem}
                placeholder="e.g., FA001"
                onChange={(e) => e.target.value = e.target.value.toUpperCase()}
                style={{ textTransform: 'uppercase' }}
              />
            </div>
            <div>
              <Label>Area Text</Label>
              <Input name="area_text" defaultValue={editingItem?.area_text} required placeholder="e.g., Production" />
            </div>
            <div>
              <Label>Area Currency</Label>
              <Select value={selectedCurrency} onValueChange={setSelectedCurrency} required>
                <SelectTrigger>
                  <SelectValue placeholder="Select currency" />
                </SelectTrigger>
                <SelectContent>
                  {currencies.map(curr => (
                    <SelectItem key={curr.currency_code} value={curr.currency_code}>
                      {curr.currency_code} - {curr.currency_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <input type="hidden" name="area_currency" value={selectedCurrency} />
            </div>
          </React.Fragment>
        );

      case 'segments':
        return (
          <React.Fragment>
            <div>
              <Label>Segment ID</Label>
              <Input 
                name="segment_id" 
                defaultValue={editingItem?.segment_id} 
                required 
                disabled={!!editingItem}
                placeholder="e.g., SEG001"
                onChange={(e) => e.target.value = e.target.value.toUpperCase()}
                style={{ textTransform: 'uppercase' }}
              />
            </div>
            <div>
              <Label>Description</Label>
              <Input name="description" defaultValue={editingItem?.description} placeholder="Optional description" />
            </div>
          </React.Fragment>
        );

      case 'chart-of-accounts':
        return (
          <React.Fragment>
            <div>
              <Label>Chart of Accounts Code</Label>
              <Input 
                name="coa_code" 
                defaultValue={editingItem?.coa_code} 
                required 
                disabled={!!editingItem}
                placeholder="e.g., COA01"
                onChange={(e) => e.target.value = e.target.value.toUpperCase()}
                style={{ textTransform: 'uppercase' }}
              />
            </div>
            <div>
              <Label>COA Name</Label>
              <Input name="coa_name" defaultValue={editingItem?.coa_name} required placeholder="e.g., Standard Chart" />
            </div>
            <div>
              <Label>Description</Label>
              <Input name="description" defaultValue={editingItem?.description} placeholder="Optional description" />
            </div>
          </React.Fragment>
        );

      case 'fiscal-year-variants':
        return (
          <React.Fragment>
            <div>
              <Label>Fiscal Year Variant</Label>
              <Input 
                name="fiscal_year_variant" 
                defaultValue={editingItem?.fiscal_year_variant} 
                required 
                disabled={!!editingItem}
                placeholder="e.g., FY01"
                onChange={(e) => e.target.value = e.target.value.toUpperCase()}
                style={{ textTransform: 'uppercase' }}
              />
            </div>
            <div>
              <Label>Description</Label>
              <Input name="description" defaultValue={editingItem?.description} required placeholder="e.g., Calendar Year" />
            </div>
            <div>
              <Label>Year Type</Label>
              <Select value={selectedYearType} onValueChange={setSelectedYearType} required>
                <SelectTrigger>
                  <SelectValue placeholder="Select year type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Calendar Year">Calendar Year</SelectItem>
                  <SelectItem value="Year Dependent">Year Dependent</SelectItem>
                </SelectContent>
              </Select>
              <input type="hidden" name="year_type" value={selectedYearType} />
            </div>
            <div>
              <Label>Number of Postings</Label>
              <Input 
                name="number_of_postings" 
                type="number" 
                min="1"
                defaultValue={editingItem?.number_of_postings} 
                required 
                placeholder="e.g., 12"
              />
            </div>
            <div>
              <Label>Number of Special Postings</Label>
              <Input 
                name="number_of_special_postings" 
                type="number" 
                min="0"
                defaultValue={editingItem?.number_of_special_postings} 
                placeholder="e.g., 4 (optional)"
              />
            </div>
          </React.Fragment>
        );

      default:
        return null;
    }
  };

  const getCurrentData = () => {
    const dataMap = {
      'credit-control-areas': data.creditControlAreas,
      'business-areas': data.businessAreas,
      'consolidated-business-areas': data.consolidatedBusinessAreas,
      'functional-areas': data.functionalAreas,
      'segments': data.segments,
      'chart-of-accounts': data.chartOfAccounts,
      'fiscal-year-variants': data.fiscalYearVariants,
    };
    return dataMap[activeTab] || [];
  };

  const getTableColumns = () => {
    const columnsMap = {
      'credit-control-areas': ['ID', 'Currency', 'Credit Limit', 'Actions'],
      'business-areas': ['ID', 'Name', 'Description', 'Actions'],
      'consolidated-business-areas': ['ID', 'Name', 'Description', 'Actions'],
      'functional-areas': ['ID', 'Area Text', 'Currency', 'Actions'],
      'segments': ['ID', 'Description', 'Actions'],
      'chart-of-accounts': ['COA ID', 'Name', 'Type', 'Currency', 'Accounts', 'Actions'],
      'fiscal-year-variants': ['ID', 'Description', 'Flags', 'Posting Periods', 'Special Periods', 'Total Periods', 'Actions'],
    };
    return columnsMap[activeTab] || [];
  };

  const getTableRow = (item) => {
    switch (activeTab) {
      case 'credit-control-areas':
        return (
          <>
            <td className="py-3 px-4 font-mono text-sm">{item.credit_control_area}</td>
            <td className="py-3 px-4 text-sm">{item.currency}</td>
            <td className="py-3 px-4 text-sm">{item.credit_limit?.toLocaleString()}</td>
          </>
        );
      case 'business-areas':
        return (
          <>
            <td className="py-3 px-4 font-mono text-sm">{item.business_area}</td>
            <td className="py-3 px-4 text-sm">{item.name}</td>
            <td className="py-3 px-4 text-sm">{item.description || '-'}</td>
          </>
        );
      case 'consolidated-business-areas':
        return (
          <>
            <td className="py-3 px-4 font-mono text-sm">{item.consolidated_business_area}</td>
            <td className="py-3 px-4 text-sm">{item.name}</td>
            <td className="py-3 px-4 text-sm">{item.description || '-'}</td>
          </>
        );
      case 'functional-areas':
        return (
          <>
            <td className="py-3 px-4 font-mono text-sm">{item.functional_area}</td>
            <td className="py-3 px-4 text-sm">{item.area_text}</td>
            <td className="py-3 px-4 text-sm">{item.area_currency}</td>
          </>
        );
      case 'segments':
        return (
          <>
            <td className="py-3 px-4 font-mono text-sm">{item.segment_id}</td>
            <td className="py-3 px-4 text-sm">{item.description || '-'}</td>
          </>
        );
      case 'chart-of-accounts':
        return (
          <>
            <td className="py-3 px-4 font-mono text-sm font-semibold">{item.coa_id}</td>
            <td className="py-3 px-4 text-sm">
              <div>{item.name}</div>
              {item.description && (
                <div className="text-xs text-gray-500">{item.description}</div>
              )}
            </td>
            <td className="py-3 px-4 text-sm">
              <span className={`px-2 py-1 text-xs rounded ${
                item.coa_type === 'operational' ? 'bg-orange-100 text-orange-700' :
                item.coa_type === 'group' ? 'bg-orange-100 text-orange-700' :
                'bg-green-100 text-green-700'
              }`}>
                {item.coa_type?.replace('-', ' ') || 'operational'}
              </span>
            </td>
            <td className="py-3 px-4 text-sm font-mono">{item.currency || '-'}</td>
            <td className="py-3 px-4 text-sm">
              <span className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded">
                {item.accounts?.length || 0} accounts
              </span>
            </td>
          </>
        );
      case 'fiscal-year-variants':
        return (
          <>
            <td className="py-3 px-4 font-mono text-sm font-semibold">{item.fiscal_year_variant_id}</td>
            <td className="py-3 px-4 text-sm">{item.description}</td>
            <td className="py-3 px-4 text-sm">
              <div className="flex gap-1">
                {item.calendar_year_flag && (
                  <span className="px-2 py-1 bg-orange-100 text-orange-700 text-xs rounded">Calendar</span>
                )}
                {item.year_dependent_flag && (
                  <span className="px-2 py-1 bg-orange-100 text-orange-700 text-xs rounded">Year Dependent</span>
                )}
              </div>
            </td>
            <td className="py-3 px-4 text-sm">{item.number_of_posting_periods}</td>
            <td className="py-3 px-4 text-sm">{item.number_of_special_periods}</td>
            <td className="py-3 px-4 text-sm font-mono text-gray-500">{item.periods?.length || 0} periods</td>
          </>
        );
      default:
        return null;
    }
  };

  const tabs = [
    { id: 'credit-control-areas', label: 'Credit Control Areas' },
    { id: 'business-areas', label: 'Business Areas' },
    { id: 'consolidated-business-areas', label: 'Consolidated Business Areas' },
    { id: 'functional-areas', label: 'Functional Areas' },
    { id: 'segments', label: 'Segments' },
    { id: 'chart-of-accounts', label: 'Chart of Accounts' },
    { id: 'fiscal-year-variants', label: 'Fiscal Year Variants' },
  ];

  return (
    <Layout>
      <div className="p-6 max-w-7xl mx-auto">
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-gray-900">Finance</h2>
          <p className="text-sm text-gray-500 mt-1">Manage finance module entities</p>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 mb-6">
          <div className="flex overflow-x-auto">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => {
                  setActiveTab(tab.id);
                  setEditingItem(null);
                  setIsDialogOpen(false);
                }}
                className={`px-6 py-4 font-medium whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'text-green-600 border-b-2 border-green-600'
                    : 'text-gray-600'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        <Card className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-xl font-semibold text-gray-900">
              {tabs.find(t => t.id === activeTab)?.label}
            </h3>
            <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
              {canWrite(activeTab) && (
                <DialogTrigger asChild>
                  <Button type="button" data-testid="btn-add-new" onClick={() => setEditingItem(null)}>
                    <Plus className="w-4 h-4 mr-2" />
                    Add New
                  </Button>
                </DialogTrigger>
              )}
              <DialogContent className={`max-h-[90vh] overflow-y-auto ${(activeTab === 'fiscal-year-variants' || activeTab === 'chart-of-accounts') ? 'max-w-5xl' : ''}`}>
                <DialogHeader>
                  <DialogTitle>
                    {editingItem ? 'Edit' : 'Create'} {tabs.find(t => t.id === activeTab)?.label}
                  </DialogTitle>
                </DialogHeader>
                {activeTab === 'fiscal-year-variants' && (
                  <FiscalYearVariantForm
                    key={`fyv-form-${editingItem?.fiscal_year_variant_id || 'new'}`}
                    editingItem={editingItem}
                    onSubmit={handleFYVSubmit}
                    onCancel={() => {
                      setIsDialogOpen(false);
                      setEditingItem(null);
                    }}
                  />
                )}
                {activeTab === 'chart-of-accounts' && (
                  <ChartOfAccountsForm
                    key={`coa-form-${editingItem?.coa_id || 'new'}`}
                    editingItem={editingItem}
                    onSubmit={handleCOASubmit}
                    onCancel={() => {
                      setIsDialogOpen(false);
                      setEditingItem(null);
                    }}
                  />
                )}
                {activeTab !== 'fiscal-year-variants' && activeTab !== 'chart-of-accounts' && (
                  <form onSubmit={handleSubmit} className="space-y-4">
                    {getFormFields()}
                    <div className="flex gap-3 pt-4">
                      <Button type="submit" className="flex-1">
                        {editingItem ? 'Update' : 'Create'}
                      </Button>
                      <Button 
                        type="button" 
                        variant="outline" 
                        onClick={() => {
                          setIsDialogOpen(false);
                          setEditingItem(null);
                        }}
                      >
                        Cancel
                      </Button>
                    </div>
                  </form>
                )}
              </DialogContent>
            </Dialog>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  {getTableColumns().map((col, idx) => (
                    <th key={idx} className="text-left py-3 px-4 text-sm font-semibold text-gray-700">
                      {col}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {getCurrentData().length === 0 ? (
                  <tr>
                    <td colSpan={getTableColumns().length} className="text-center py-8 text-gray-500">
                      No records found
                    </td>
                  </tr>
                ) : (
                  getCurrentData().map((item) => (
                    <tr key={item[getIdField()]} className="border-b border-gray-100 hover:bg-gray-50">
                      {getTableRow(item)}
                      <td className="py-3 px-4 text-right">
                        <div className="flex gap-2 justify-end">
                          <RecordActions
                            doctype={activeTab}
                            idForTestId={item[getIdField()]}
                            onView={() => setViewItem(item)}
                            onEdit={() => { setEditingItem(item); setIsDialogOpen(true); }}
                          />
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </Card>
      </div>

      <RecordViewDialog
        open={!!viewItem}
        onOpenChange={(o) => { if (!o) setViewItem(null); }}
        title={`${tabs.find(t => t.id === activeTab)?.label || 'Record'} Details`}
        record={viewItem}
      />
    </Layout>
  );
};

export default Finance;
